/**
 * MediaLibraryPanel - Free stock media search and download panel
 * Polished Canva/Wix-inspired design with tab navigation
 */

import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  Download,
  CheckSquare,
  Square,
  Image,
  Video,
  Music,
  FolderOpen,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MediaLibraryForm } from "./media-library-form";
import { ImagesList } from "./images-list";
import { VideosList } from "./videos-list";
import { SoundsList } from "./sounds-list";
import { DownloadedAssetsSection } from "./downloaded-assets-section";
import { DownloadingItemCard } from "./downloading-item-card";
import { useMediaLibrary } from "./use-media-library";
import { MediaLibraryPanelProps } from "@/types/media-library-panel.types";
import type { MediaType } from "@/types/media-library-panel.types";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/hooks/useResponsive";

type TabId = "image" | "video" | "audio" | "downloaded";

const TABS: Array<{
  id: TabId;
  label: string;
  icon: typeof Image;
}> = [
  { id: "image", label: "Images", icon: Image },
  { id: "video", label: "Videos", icon: Video },
  { id: "audio", label: "Sounds", icon: Music },
  { id: "downloaded", label: "", icon: FolderOpen },
];

export default function MediaLibraryPanel({
  devContainerId,
  projectId,
  previewUrl,
  onBack,
}: MediaLibraryPanelProps): React.ReactElement {
  const {
    query,
    setQuery,
    mediaType: _mediaType,
    setMediaType,
    provider,
    setProvider,
    orientation,
    setOrientation,
    color,
    setColor,
    sortOrder,
    setSortOrder,
    videoSize,
    setVideoSize,
    videoType,
    setVideoType,
    searchResults,
    totalResults,
    isSearching,
    isShowingCurated,
    handleSearch,
    hasMore,
    isLoadingMore,
    loadMore,
    selectedResults,
    toggleResultSelection,
    clearResultSelection,
    selectAllResults,
    downloadingItems,
    downloadingIds,
    handleDownload,
    handleDownloadSelected,
    projectAssets,
    totalProjectAssets,
    projectAssetsCountByType,
    isLoadingAssets,
    loadProjectAssets,
    assetsHasMore,
    isLoadingMoreAssets,
    loadMoreAssets,
    selectedAssets,
    toggleAssetSelection,
    clearAssetSelection,
    selectAllAssets,
    isDeleting,
    handleDeleteSelected,
    scrollViewportRef,
  } = useMediaLibrary({ devContainerId, projectId, previewUrl });

  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<TabId>("image");

  const isDownloadedTab = activeTab === "downloaded";
  const currentMediaType: MediaType =
    activeTab === "downloaded" ? "image" : activeTab;

  const hasResults = searchResults.length > 0;
  const hasDownloading = downloadingItems.length > 0;
  const hasSelectedResults = selectedResults.size > 0;

  // Lazy-load downloaded assets only when the Downloaded tab is first opened
  const assetsLoadedRef = useRef(false);
  useEffect(() => {
    if (isDownloadedTab && !assetsLoadedRef.current) {
      assetsLoadedRef.current = true;
      loadProjectAssets();
    }
  }, [isDownloadedTab, loadProjectAssets]);

  // Latch — once the Downloaded tab has been opened, keep its JSX mounted
  // even while the user is on other tabs. Hiding with CSS preserves every
  // `<img>` and `<video>` element (and their already-fetched pixel data)
  // so switching back to Downloaded reveals cached thumbnails instantly
  // instead of re-requesting each R2 asset.
  const [hasOpenedDownloaded, setHasOpenedDownloaded] = useState(false);
  useEffect(() => {
    if (isDownloadedTab && !hasOpenedDownloaded) {
      setHasOpenedDownloaded(true);
    }
  }, [isDownloadedTab, hasOpenedDownloaded]);

  const handleTabChange = (tabId: TabId): void => {
    setActiveTab(tabId);
    if (tabId !== "downloaded") {
      setMediaType(tabId as MediaType);
    }
  };

  return (
    <div
      className={cn("flex flex-col h-full", isMobile ? "" : "bg-background")}
    >
      {/* Header */}
      <div
        className={cn(
          "px-4 border-b border-border flex items-center gap-2 bg-background",
          isMobile ? "h-10" : "h-11",
        )}
      >
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-muted-foreground transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Media Library</span>
        </button>
      </div>

      {/* Tab bar - prominent, well-spaced */}
      <div className="flex items-stretch border-b border-border bg-background">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "relative flex items-center justify-center gap-1.5 transition-colors",
                tab.id === "downloaded"
                  ? "ml-auto px-3 py-2.5"
                  : "flex-1 py-2.5",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className={cn("w-4 h-4", isActive && "text-primary")} />
              {tab.label && (
                <span
                  className={cn(
                    "text-xs font-medium",
                    isActive && "font-semibold",
                  )}
                >
                  {tab.label}
                </span>
              )}
              {tab.id === "downloaded" && totalProjectAssets > 0 && (
                <span
                  className={cn(
                    "min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none flex items-center justify-center",
                    isActive
                      ? "bg-primary/15 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {totalProjectAssets}
                </span>
              )}
              {/* Active indicator */}
              {isActive && (
                <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>

      {/* Search + filters - fixed at top */}
      {!isDownloadedTab && (
        <div className={cn(isMobile ? "px-3 py-2.5" : "px-4 py-3")}>
          <MediaLibraryForm
            query={query}
            onQueryChange={setQuery}
            mediaType={currentMediaType}
            provider={provider}
            onProviderChange={setProvider}
            orientation={orientation}
            onOrientationChange={setOrientation}
            color={color}
            onColorChange={setColor}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
            videoSize={videoSize}
            onVideoSizeChange={setVideoSize}
            videoType={videoType}
            onVideoTypeChange={setVideoType}
            isSearching={isSearching}
            onSearch={handleSearch}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Results header bar */}
      {!isDownloadedTab && hasResults && (
        <div
          className={cn(
            "flex items-center justify-between border-y border-border/40 bg-muted/30",
            isMobile ? "px-3 py-1.5" : "px-4 py-1.5",
          )}
        >
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            {isShowingCurated ? "Trending" : "Results"}{" "}
            <span className="text-muted-foreground/60 font-medium normal-case tracking-normal">
              ({totalResults.toLocaleString()})
            </span>
          </span>
          <button
            onClick={
              hasSelectedResults ? clearResultSelection : selectAllResults
            }
            className={cn(
              "p-1 rounded-md transition-colors",
              hasSelectedResults
                ? "text-primary hover:bg-primary/10"
                : "text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted",
            )}
            title={hasSelectedResults ? "Deselect all" : "Select all"}
          >
            {hasSelectedResults ? (
              <CheckSquare className="w-3.5 h-3.5" />
            ) : (
              <Square className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      )}

      {/* Content area - scrollable. `min-w-0` on the scrollable div lets the
          flex-based masonry inside shrink correctly — without it, the panel's
          flex chain can cause the masonry to escape the viewport horizontally. */}
      <ScrollArea className="flex-1 min-w-0" viewportRef={scrollViewportRef}>
        <div className={cn("min-w-0 w-full", isMobile ? "p-2" : "p-3")}>
          {/* Downloaded tab: mounted once and kept alive via CSS `hidden`.
              Unmounting would destroy every <img>/<video> in the gallery
              and cause the browser to re-request each R2 asset when the
              user switches back to this tab. */}
          {hasOpenedDownloaded && (
            <div className={cn(!isDownloadedTab && "hidden")}>
              <DownloadedAssetsSection
                assets={projectAssets}
                totalAssets={totalProjectAssets}
                countByType={projectAssetsCountByType}
                selectedIds={selectedAssets}
                onToggleSelection={toggleAssetSelection}
                onSelectAll={selectAllAssets}
                onClearSelection={clearAssetSelection}
                onDeleteSelected={handleDeleteSelected}
                isLoading={isLoadingAssets}
                isDeleting={isDeleting}
                hasMore={assetsHasMore}
                isLoadingMore={isLoadingMoreAssets}
                onLoadMore={loadMoreAssets}
              />
            </div>
          )}

          {/* Search side — only mounted while an active search tab is
              selected; provider search results don't need the mount-latch
              treatment since re-issuing a search is cheap and the content
              changes on every query. */}
          {!isDownloadedTab && (
            <div className="space-y-3">
              {hasDownloading && (
                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Downloading{" "}
                    <span className="text-muted-foreground/60 font-medium normal-case tracking-normal">
                      ({downloadingItems.length})
                    </span>
                  </span>
                  <div className="space-y-1">
                    {downloadingItems.map((item) => (
                      <DownloadingItemCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              )}

              {currentMediaType === "image" && (
                <ImagesList
                  results={searchResults}
                  onDownload={(item) => handleDownload([item])}
                  selectedIds={selectedResults}
                  onToggleSelect={toggleResultSelection}
                  downloadingIds={downloadingIds}
                  isLoading={isSearching}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                />
              )}
              {currentMediaType === "video" && (
                <VideosList
                  results={searchResults}
                  onDownload={(item) => handleDownload([item])}
                  selectedIds={selectedResults}
                  onToggleSelect={toggleResultSelection}
                  downloadingIds={downloadingIds}
                  isLoading={isSearching}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                />
              )}
              {currentMediaType === "audio" && (
                <SoundsList
                  results={searchResults}
                  onDownload={(item) => handleDownload([item])}
                  selectedIds={selectedResults}
                  onToggleSelect={toggleResultSelection}
                  downloadingIds={downloadingIds}
                  isLoading={isSearching}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                />
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Sticky download footer */}
      {hasSelectedResults && !isDownloadedTab && (
        <div className="px-4 py-2.5 border-t border-border bg-background flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground">
            {selectedResults.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              onClick={clearResultSelection}
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs"
            >
              Clear
            </Button>
            <Button
              onClick={handleDownloadSelected}
              size="sm"
              className="h-8 px-4 text-xs rounded-full gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Download ({selectedResults.size})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
